import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { CustomerProfile } from '../customer-profile.js';
import { TicketReservationItem } from './ticket-reservation-item.js';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export enum TicketReservationStatus {
  Live = 'LIVE',
  Canceled = 'CANCELED',
  Sold = 'SOLD',
}

export class TicketReservation extends DataModel {
  static readonly TYPE_ID: string = '215e1b185c0a47d2a467de6b3eee8d2a';

  public readonly userId: string | null;
  public readonly customerId: string;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: TicketReservationStatus,
    public readonly eventId: string,
    public readonly customer: CustomerProfile,
    public readonly items: TicketReservationItem[],
  ) {
    super();
    this.userId = customer.userId;
    this.customerId = customer.uniqueId;
  }

  get $typeId() {
    return TicketReservation.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketReservation {
    return new TicketReservation(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, 'status'), TicketReservationStatus),
        MapUtils.get<string>(map, 'eventId'),
        context.fromData<CustomerProfile>(MapUtils.get<SerializedData>(map, 'customer')),
        MapUtils.getList<TicketReservationItem>(map, 'items', (value) => {
          return context.fromData<TicketReservationItem>(value as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'businessRef': this.businessRef.encode(),
      'status': fromEnumAsString(this.status),
      'eventId': this.eventId,
      'userId': this.userId,
      'customerId': this.customerId,
      'customer': context.toData(this.customer),
      'items': this.items.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new TicketReservation(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.status,
        this.eventId,
        this.customer.clone(),
        this.items.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: TicketReservation): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.creationDate === this.creationDate &&
      isSameData(other.businessRef, this.businessRef) &&
      other.status === this.status &&
      other.eventId === this.eventId &&
      other.userId === this.userId &&
      other.customerId === this.customerId &&
      isSameData(other.customer, this.customer) &&
      isSameList(other.items, this.items);
  }

  copyWithStatus(status: TicketReservationStatus): TicketReservation {
    return new TicketReservation(
        this.uniqueId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        status,
        this.eventId,
        this.customer.clone(),
        this.items.map((x) => x.clone()),
    );
  }
}

export class TicketReservationBuilder extends DataModelBuilder<TicketReservation> {
  build(context: SerializationContext, map: SerializedData): TicketReservation {
    return TicketReservation.fromMap(context, map);
  }
}
